import { useMemo, useEffect, useState } from 'react';
import { message } from 'antd';
import { observer } from 'mobx-react-lite';
import Header from '@components/header/Header';
import Editor from '@components/editor/Editor';
import { ConfigProvider, theme } from 'antd';
import { StyleProvider } from '@ant-design/cssinjs';
import SideBar from '@components/sideBar/SideBar';
import ToolBar from '@components/sideBar/ToolBar';
import Init from '@components/init/Init';
import LandingPage from '@components/LandingPage';
import stores from '@stores';
import useSetImg from '@hooks/useSetImg';
import { cn } from '@utils/utils';
import '@style/main.css';
// 导入IndexedDB管理器
import dbManager from './utils/IndexedDBManager';

// 使用sessionStorage存储当前编辑的图片数据
const EDIT_IMAGE_KEY = 'currentEditImage';

export default observer(({ defaultImg, headLeft, headRight, isDark, boxClassName = '', onClear }) => {
  const [isEditorActive, setIsEditorActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);  // 添加加载状态
  const getFile = useSetImg(stores);
  const workplace = stores.editor.img?.src ? <Editor /> : <Init />
  const [messageApi, contextHolder] = message.useMessage();
  stores.editor.setMessage(messageApi);
  stores.editor.setClearFun(() => {
    if (onClear) onClear();
    setIsEditorActive(false);
  });
  
  useMemo(() => {
    const mode = isDark || localStorage.getItem('SHOTEASY_BEAUTIFIER_THEME') === 'dark' ? 'dark' : 'light';
    stores.editor.setTheme(mode);
  }, [isDark]);
  
  useEffect(() => {
    if (defaultImg) {
      getFile(defaultImg, 'dataURL');
      setIsEditorActive(true);
    }
  }, [defaultImg]);

  // 检查URL参数是否直接开启编辑器
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isEditorRequested = urlParams.get('editor') === 'true';
    
    if (isEditorRequested) {
      setIsEditorActive(true);
      
      // 检查是否是从视频截图页面过来的
      const source = urlParams.get('source');
      if (source === 'videoCapture') {
        setIsLoading(true);
        
        // 检查是否通过ID参数方式加载
        const snapshotId = urlParams.get('snapshotId');
        if (snapshotId) {
          // 从IndexedDB读取截图
          dbManager.getSnapshot(snapshotId)
            .then(snapshot => {
              if (snapshot && snapshot.dataUrl) {
                // 加载图片到编辑器
                return getFile(snapshot.dataUrl, 'dataURL');
              } else {
                throw new Error('未能找到对应的截图数据');
              }
            })
            .then(() => {
              messageApi.success('截图已加载到编辑器');
            })
            .catch(err => {
              console.error('从IndexedDB加载截图失败:', err);
              messageApi.error('加载截图失败，请重试');
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
          // 尝试从 sessionStorage 获取待编辑的图片
          const pendingImage = sessionStorage.getItem(EDIT_IMAGE_KEY);
          if (pendingImage) {
            // 加载图片到编辑器
            getFile(pendingImage, 'dataURL').then(() => {
              // 加载成功后清除 sessionStorage 中的数据
              sessionStorage.removeItem(EDIT_IMAGE_KEY);
              messageApi.success('截图已加载到编辑器');
            }).catch(err => {
              console.error('加载截图失败:', err);
              messageApi.error('加载截图失败，请重试');
            }).finally(() => {
              setIsLoading(false);
            });
          } else {
            setIsLoading(false);
            messageApi.warning('未找到要编辑的图片');
          }
        }
      } else {
        setIsLoading(false);
      }
    }
  }, []);

  // 处理从着陆页转到编辑器的函数
  const handleStartEditing = () => {
    setIsEditorActive(true);
    // 可选：更新URL以反映状态变化
    const url = new URL(window.location);
    url.searchParams.set('editor', 'true');
    window.history.pushState({}, '', url);
  };

  return (
    <StyleProvider>
      <ConfigProvider
        theme={{
          algorithm: stores.editor.isDark ? theme.darkAlgorithm : theme.defaultAlgorithm
        }}
      >
        {contextHolder}
        {isEditorActive ? (
          <div id="shoteasy-container" className={cn("flex flex-col overflow-hidden antialiased w-full h-[100vh] bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800", boxClassName)} data-mode={stores.editor.isDark?'dark':'light'}>
            <Header headLeft={headLeft} headRight={headRight} />
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="ant-spin ant-spin-lg ant-spin-spinning">
                    <span className="ant-spin-dot ant-spin-dot-spin">
                      <i className="ant-spin-dot-item"></i>
                      <i className="ant-spin-dot-item"></i>
                      <i className="ant-spin-dot-item"></i>
                      <i className="ant-spin-dot-item"></i>
                    </span>
                  </div>
                  <div className="mt-3 text-gray-500">加载图片中...</div>
                </div>
              </div>
            ) : (
              <div className="flex flex-1 h-0 md:flex-row md:items-stretch">
                <ToolBar />
                {workplace}
                <SideBar />
              </div>
            )}
          </div>
        ) : (
          <LandingPage onStartEditing={handleStartEditing} />
        )}
      </ConfigProvider>
    </StyleProvider>
  )
});